import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { formatNumber } from "../../utils/common";
import { Box, Typography } from "@mui/material";
import styles from "../../styles/common/barChart.module.css";
 
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);
 
/** Clean horizontal chart: y grid off + left spine; x grid lines use options (solid/dash). */
const horizontalCleanNoGridPlugin = {
  id: "horizontalCleanNoGrid",
  beforeUpdate(chart) {
    const y = chart.options?.scales?.y;
    if (y) {
      const yPrev = typeof y.grid === "object" && y.grid !== null ? y.grid : {};
      y.grid = {
        ...yPrev,
        display: false,
        lineWidth: 0,
        drawOnChartArea: false,
        drawTicks: false,
        borderWidth: 0,
        color: "transparent",
      };
      const yBorder =
        typeof y.border === "object" && y.border !== null ? y.border : {};
      y.border = {
        ...yBorder,
        display: true,
        color: yBorder.color ?? "rgba(148, 163, 184, 0.55)",
        width: yBorder.width ?? 1,
      };
    }
  },
};
 
const BarChart = ({
  labels,
  datasets,
  customWidth,
  customHeight,
  yAxisTitle,
  xAxisTitle,
  display,
  ellipsis,
  stacked,
  yMax,
  yStep,
  indexAxis,
  /** Formats numeric axis ticks; used on y (vertical bars) or x (horizontal bars) */
  linearTickFormatter,
  /** Reference layout: dashed vertical grid, no horizontal grid, flat bars, axis title */
  horizontalCleanStyle,
  /** Label on the numeric axis when horizontalCleanStyle (default "Emissions") */
  valueAxisTitle,
}) => {
  const isHorizontal = (indexAxis || "x") === "y";
  const cleanHorizontal = Boolean(horizontalCleanStyle && isHorizontal);
  const numericAxisTitle =
    valueAxisTitle !== undefined && valueAxisTitle !== null
      ? valueAxisTitle
      : "Emissions";
 
  const formatLinearTick = (value) =>
    typeof linearTickFormatter === "function"
      ? linearTickFormatter(value)
      : formatNumber(value);
 
  const data = {
    labels: labels || [
      "Electronics",
      "Furniture",
      "Clothing",
      "Automobiles",
      "Home Appliances",
    ],
    datasets: datasets
      ? datasets?.filter(Boolean).map((ds) =>
          cleanHorizontal
            ? { ...ds, borderRadius: 0, borderWidth: 0 }
            : ds
        )
      : [
          {
            label: "",
            data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            backgroundColor: "rgba(75, 192, 192, 0.6)",
            borderColor: "rgba(75, 192, 192, 1)",
            borderWidth: 1,
          },
        ],
  };
 
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: indexAxis || "x",
    plugins: {
      legend: {
        display: display || false,
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 16,
          boxWidth: 8,
          boxHeight: 8,
          font: {
            family: "Inter, sans-serif",
            size: 12,
            weight: "500",
            lineHeight: 1.2,
          },
        },
      },
      title: {
        display: !cleanHorizontal,
      },
      datalabels: {
        display: false,
      },
      ...(typeof linearTickFormatter === "function"
        ? {
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const raw = isHorizontal ? ctx.parsed.x : ctx.parsed.y;
                  const prefix = ctx.dataset?.label
                    ? `${ctx.dataset.label}: `
                    : "";
                  return `${prefix}${linearTickFormatter(raw)}`;
                },
              },
            },
          }
        : {}),
    },
    ...(cleanHorizontal
      ? {
          elements: {
            bar: {
              borderRadius: 0,
            },
          },
        }
      : {}),
    scales: {
      x: {
        stacked: stacked || false,
        categoryPercentage: 0.6, // Controls the width of category (group of bars)
        barPercentage: 0.8, // Controls the width of individual bars within category
        ...(!cleanHorizontal && {
          title: {
            display: Boolean(xAxisTitle),
            text: xAxisTitle || "",
          },
        }),
        ...(cleanHorizontal && {
          border: {
            display: false,
            dash: (ctx) => (ctx.index === 0 ? [] : [4, 4]),
            dashOffset: 0,
          },
          title: {
            display: Boolean(numericAxisTitle),
            text: numericAxisTitle,
            color: "#64748b",
            font: { size: 12, weight: "500", family: "Inter, sans-serif" },
            padding: { top: 10 },
          },
        }),
        ticks: {
          callback: function (value) {
            if (isHorizontal) {
              return formatLinearTick(value);
            }
            const labels = this?.chart?.data?.labels;
            if (!Array.isArray(labels) || labels[value] == null) return "";
            const label = labels[value];
 
            // If ellipsis is explicitly false, don't truncate
            if (ellipsis === false) {
              return label;
            }
 
            const shouldTruncate =
              label?.length > 15 ||
              (labels?.length > 8 && label?.length > 6) ||
              (label?.length > 7 && labels?.length > 10) ||
              (label?.length > 10 && labels?.length > 5);
 
            return shouldTruncate ? label.substring(0, 15) + "..." : label;
          },
          padding: 10,
          maxRotation: 45,
          minRotation: 0,
          ...(cleanHorizontal && {
            color: "#64748b",
            font: { size: 11, family: "Inter, sans-serif" },
          }),
        },
        grid: isHorizontal
          ? cleanHorizontal
            ? {
                display: true,
                drawBorder: false,
                drawTicks: false,
                drawOnChartArea: true,
                color: "rgba(148, 163, 184, 0.4)",
                lineWidth: 1,
              }
            : { display: false }
          : { display: false },
      },
      y: {
        stacked: stacked || false,
        title: yAxisTitle !== "" && {
          display: true,
          text: yAxisTitle ? yAxisTitle : "",
        },
        beginAtZero: true,
        max: typeof yMax === "number" ? yMax : undefined,
        ...(isHorizontal && {
          grid: {
            display: false,
            drawBorder: false,
            drawOnChartArea: false,
            drawTicks: false,
            lineWidth: 0,
            borderWidth: 0,
            color: "transparent",
          },
          ...(cleanHorizontal
            ? {
                border: {
                  display: true,
                  color: "rgba(148, 163, 184, 0.55)",
                  width: 1,
                },
              }
            : {
                border: { display: false },
              }),
        }),
        ticks: {
          padding: 10, // Add space between axis and labels
          stepSize: typeof yStep === "number" ? yStep : undefined,
          ...(cleanHorizontal && {
            color: "#111827",
            font: { size: 12, family: "Inter, sans-serif" },
          }),
          callback: function (value) {
            if (isHorizontal) {
              const labels = this?.chart?.data?.labels;
              if (!Array.isArray(labels)) return String(value ?? "");
              const label =
                labels[value] != null ? labels[value] : String(value ?? "");
              if (!label) return "";
              if (ellipsis === false) {
                return label;
              }
              const shouldTruncate =
                label?.length > 15 ||
                (labels?.length > 8 && label?.length > 6) ||
                (label?.length > 7 && labels?.length > 10) ||
                (label?.length > 10 && labels?.length > 5);
              return shouldTruncate ? label.substring(0, 15) + "..." : label;
            }
            return formatLinearTick(value);
          },
        },
      },
    },
  };
 
  return (
    <div
      className={`${styles.barChartContainer} barChartContainerOverride${
        cleanHorizontal ? ` ${styles.barChartCleanHorizontal}` : ""
      }`}
      style={{
        minHeight: customHeight || "250px",
      }}
    >
      {labels?.length > 0 && datasets?.length > 0 ? (
        <Bar
          data={data}
          options={options}
          plugins={cleanHorizontal ? [horizontalCleanNoGridPlugin] : undefined}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center", // horizontally center
            alignItems: "center", // vertically center
            height: customHeight || "18rem", // ensure it takes up space
            width: customWidth || "100%", // optional, to match parent
          }}
        >
          <Typography
            sx={{
              fontSize: "24px",
              fontFamily: "inter",
              color: "#d3d3d3",
              fontWeight: "bold",
            }}
          >
            No Data Found
          </Typography>
        </Box>
      )}
    </div>
  );
};
 
export default BarChart;